/**
 * ad-entry/client — 브라우저 쪽 읽기·미러 (CL fix_7 §1 · 레인 0b).
 *
 * 순서: 미들웨어가 첫 요청에서 `kd_entry` 쿠키를 쓴다(신호가 있을 때만) → 이 모듈이 페이지마다
 * 쿠키를 sessionStorage `kd_entry` 로 미러하고, 쿠키가 없으면(직접·자연 유입, 미들웨어 미적용 경로)
 * 같은 판정을 여기서 한 번 더 해 둘 다 채운다.
 *
 * ⚠️ 모든 저장소 접근은 try/catch — 사파리 프라이빗·인앱 브라우저에서 throw 한다. 실패해도 폼·계측은 계속된다.
 * ⚠️ 레인 A(isAdVariant)는 `readEntryClient()?.src === 'naver_ad'` 로 읽는다. 서버 렌더에서 읽지 않는다(ISR).
 */
import {
  KD_ENTRY_KEY,
  KD_ENTRY_MAX_AGE_S,
  classifyEntry,
  decideEntry,
  isFresh,
  parseEntry,
  pickAttributionParams,
  serializeEntry,
  type EntryValue,
} from './index';

/** 착지 때 본 광고·utm 파라미터 전량(n_ad_group 등 계약 밖 키 포함) — 리드 query 복원용. 계약 키와 분리. */
export const KD_ENTRY_PARAMS_KEY = 'kd_entry_q';

function ssGet(k: string): string | null {
  try {
    return window.sessionStorage.getItem(k);
  } catch {
    return null;
  }
}
function ssSet(k: string, v: string) {
  try {
    window.sessionStorage.setItem(k, v);
  } catch {
    /* 저장 불가 — 쿠키만으로 간다 */
  }
}

function readCookie(name: string): string | null {
  try {
    const parts = document.cookie ? document.cookie.split('; ') : [];
    for (const p of parts) {
      const i = p.indexOf('=');
      if (i > 0 && p.slice(0, i) === name) return p.slice(i + 1);
    }
  } catch {
    /* noop */
  }
  return null;
}

function writeCookie(v: EntryValue) {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${KD_ENTRY_KEY}=${serializeEntry(v)}; Max-Age=${KD_ENTRY_MAX_AGE_S}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* noop */
  }
}

/**
 * 현재 유효한 출처. 쿠키(30분 안)가 우선이고, 없으면 탭 세션의 미러.
 * 미러는 탭이 살아 있는 동안 유지된다 — 착지 40분 뒤 같은 탭에서 낸 제출도 광고 귀속을 잃지 않게.
 */
export function readEntryClient(now: number = Date.now()): EntryValue | null {
  if (typeof window === 'undefined') return null;
  const c = parseEntry(readCookie(KD_ENTRY_KEY));
  if (c && isFresh(c, now)) return c;
  return parseEntry(ssGet(KD_ENTRY_KEY));
}

/** 착지 때 보관한 광고·utm 파라미터. 없으면 null. */
export function readStoredAttribution(): Record<string, string> | null {
  if (typeof window === 'undefined') return null;
  const raw = ssGet(KD_ENTRY_PARAMS_KEY);
  if (!raw) return null;
  try {
    const o = JSON.parse(raw);
    if (!o || typeof o !== 'object') return null;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(o as Record<string, unknown>)) if (typeof v === 'string' && v) out[k] = v;
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

/**
 * 페이지마다 부른다(멱등). 반환 = 이 호출 뒤의 유효 출처.
 *   1) 쿠키가 유효하면 그 값을 미러한다(미들웨어가 쓴 값).
 *   2) 현재 URL 의 신호를 판정한다. 리퍼러는 «탭 세션의 첫 판정» 에서만 본다 —
 *      Next 의 클라이언트 이동은 document.referrer 를 바꾸지 않아, 매번 보면 같은 검색 리퍼러로
 *      30분 뒤 광고 귀속을 organic 으로 덮어쓴다.
 *   3) 광고·카페·카카오 신호면 그 URL 의 광고·utm 파라미터 전량을 `kd_entry_q` 에 보관한다.
 */
export function captureEntryClient(now: number = Date.now()): EntryValue | null {
  if (typeof window === 'undefined') return null;
  const cookieVal = parseEntry(readCookie(KD_ENTRY_KEY));
  const mirror = parseEntry(ssGet(KD_ENTRY_KEY));
  let existing: EntryValue | null = cookieVal && isFresh(cookieVal, now) ? cookieVal : mirror;

  if (cookieVal && isFresh(cookieVal, now) && serializeEntry(cookieVal) !== (mirror ? serializeEntry(mirror) : '')) {
    ssSet(KD_ENTRY_KEY, serializeEntry(cookieVal));
  }

  let search = '';
  let referrer = '';
  let host = '';
  try {
    search = window.location.search;
    host = window.location.hostname;
    referrer = mirror ? '' : document.referrer;
  } catch {
    /* noop */
  }

  let incoming = classifyEntry(search, referrer, now, host);
  // 첫 판정인데 신호가 전혀 없으면(직접 유입·리퍼러 없음) organic 으로 «한 번» 적어 둔다.
  if (!incoming && !existing) incoming = { src: 'organic', ts: now };

  const next = decideEntry(existing, incoming, now);
  if (next) {
    // 미들웨어가 방금 같은 신호로 쓴 쿠키(같은 src·n_query)는 다시 쓰지 않는다 — ts 만 다른 중복 쓰기 방지.
    const same = existing && existing.src === next.src && existing.n_query === next.n_query
      && existing.n_media === next.n_media && existing.utm_source === next.utm_source;
    if (!same) {
      writeCookie(next);
      ssSet(KD_ENTRY_KEY, serializeEntry(next));
      existing = next;
    }
    if (next.src !== 'organic') {
      const params = pickAttributionParams(search);
      if (Object.keys(params).length) ssSet(KD_ENTRY_PARAMS_KEY, JSON.stringify(params));
    }
  }
  return existing;
}
